/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

const baseURL = '/api/foldertypetemplates'
const baseURL_v2 = '/api/v2/foldertypetemplates'

export interface AllOptionsForPicker {
  id: number
  name: string
}

export interface AllOptionsForAsyncPicker {
  elements: AllOptionsForPicker[]
  totalElements: number
}

type GetAllForTableParamsType = {
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
  id?: number[]
}

interface CreateFolderTypeRequestType {
  id?: number
  description?: string
  name?: string
  system?: boolean
  [key: string]: any
}

interface User {
  id: number
  name: string
}

export interface FolderType {
  id: number
  name: string
  description?: string
  system?: boolean
  createdAt?: number
  createdBy?: User
  updatedAt?: number
  updatedBy?: User
}

class FolderTypesApiService {
  /**
   * Fetch all folder types (legacy getAllFolderTypes)
   * @param params Optional query params (default: {sortBy: 'name', sortDesc: false})
   * @returns Promise<any> (raw API response)
   * Logic matches legacy AngularJS: apiWithLoader.one(baseURL).get(params)
   */
  async getAllFolderTypes(params?: Record<string, any>): Promise<any> {
    const query = params ? params : { sortBy: 'name', sortDesc: false }
    // Use baseURL (not v2) to match legacy
    return baseApiService.fetch(baseURL, { params: query })
  }

  async getAllForTable(params: GetAllForTableParamsType) {
    params = params ? params : { sortBy: 'name', sortDesc: false }
    return baseApiService.fetch(baseURL_v2, { params })
  }

  async getAllForPicker() {
    const promise = new Promise((resolve, reject) => {
      baseApiService.fetch(baseURL + '/all').then((data) => {
        const localData = data as AllOptionsForPicker[]
        if (localData?.length) {
          localData.sort((prev, next) => prev.name.localeCompare(next.name))
          resolve(localData)
        } else {
          reject()
        }
      })
    })

    return promise
  }

  async getOptionsForAsyncPicker(search: string, pageStart: number) {
    const params = {
      name: search || null,
      pageStart,
      pageSize: 25
    }

    return baseApiService.fetch(baseURL_v2 + '/picker', {
      method: 'GET',
      params
    })
  }

  async getById(typeId: number) {
    return baseApiService.fetch(baseURL_v2 + '/' + typeId)
  }

  async create(data: CreateFolderTypeRequestType) {
    return baseApiService.fetch(baseURL, {
      method: 'POST',
      body: { ...data }
    })
  }

  async update(typeId: number, data: CreateFolderTypeRequestType) {
    return baseApiService.fetch(baseURL + '/' + typeId, {
      method: 'PUT',
      body: { ...data }
    })
  }

  async remove(typeId: number) {
    return baseApiService.fetch(baseURL + '/' + typeId, {
      method: 'DELETE'
    })
  }

  async folderUsage(typeId: number) {
    return baseApiService.fetch(baseURL + '/' + typeId + '/usage')
  }

  async docTypesUsedInFolderHierarchy(docTypeIds: number[], folderId: number) {
    const reqObj = {
      docTemplateId: docTypeIds,
      folderId: folderId
    }
    return baseApiService.fetch(baseURL + '/doctypeusedinfolder', {
      method: 'POST',
      body: reqObj
    })
  }

  async getAll(params?: Record<string, any>): Promise<any> {
    const query = params ? params : { sortBy: 'name', sortDesc: false }
    // Use baseURL (not v2) to match legacy
    return baseApiService.fetch(baseURL, { params: query })
  }
}

export const folderTypesApiService = new FolderTypesApiService()

export async function getAllForPicker(): Promise<AllOptionsForPicker[]> {
  return new Promise<AllOptionsForPicker[]>((resolve, reject) => {
    baseApiService.fetch(baseURL + '/all').then((data) => {
      const localData = data as AllOptionsForPicker[]
      if (localData?.length) {
        localData.sort((prev, next) => prev.name.toLowerCase().localeCompare(next.name.toLowerCase()))
        resolve(localData)
      } else {
        reject()
      }
    })
  })
}
